/**
 * @fileoverview Root route configuration for the application
 * Provides essential providers and layout structure
 */

// Library imports
import { Outlet, createRootRoute } from "@tanstack/react-router";

// Application providers
import { QueryProviders } from "@/providers/query.provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { AppErrorBoundary, AppNotFound } from "@/components/shared/app-error-boundary";

/**
 * Root layout component that wraps the entire application.
 * Only cross-surface, auth-agnostic providers belong here (query client, tooltips, toasts).
 * Auth/session providers are mounted by the auth and dashboard surfaces, not globally, so
 * public marketing routes never instantiate the auth context or session claims fetch.
 */
const RootLayout = () => {
  return (
    <main
      className="flex min-h-screen w-full"
      role="main"
      aria-label="Main application layout"
    >
      <QueryProviders>
        <TooltipProvider delayDuration={200}>
          <div className="flex min-h-screen w-full">
            <Outlet />
          </div>
          <Toaster position="bottom-right" richColors closeButton />
        </TooltipProvider>
      </QueryProviders>
    </main>
  );
};

// Root route configuration
export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: AppErrorBoundary,
  notFoundComponent: AppNotFound,
});
